import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  FieldError,
  Form,
  Input,
  Label,
  ListBox,
  Select,
  Switch,
  TextField,
} from "@heroui/react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";

import {
  useDirectionsFrom,
  useDirectionsTo,
} from "@/features/directions/use-directions.ts";
import { useTariffs } from "@/features/tariffs/use-tariffs.ts";
import { useOrdersCreate } from "@/features/order/use-orders.ts";

export const Route = createFileRoute("/dashboard/order/add")({
  component: RouteComponent,
});

interface IFormInput {
  fromCityId: number;
  fromLatitude: number;
  fromLongitude: number;
  fromAddress: string;
  fromApartment: string;
  fromEntrance: string;
  fromFloor: string;

  toCityId: number;
  toLatitude: number;
  toLongitude: number;
  toAddress: string;
  toApartment: string;
  toEntrance: string;
  toFloor: string;

  tariffId: number;

  description: string;
  category: string;
  isFragile: boolean;
  isDefect: boolean;
  photos: string[];
  contentPhotos: string[];
  comment: string;

  width: number;
  length: number;
  height: number;
  weight: number;
}

function RouteComponent() {
  const [selectedCityFromKey, setSelectedCityFromKey] = useState<string | null>(null);

  const tariffs = useTariffs({});
  const citiesFrom = useDirectionsFrom({ enabled: true });
  const citiesTo = useDirectionsTo({
    enabled: !!selectedCityFromKey,
    fromCityId: selectedCityFromKey ? Number(selectedCityFromKey) : undefined,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const selectedTariffId = watch("tariffId");
  const selectedTariff = tariffs.data.data.find(
    (item) => item.id === Number(`${selectedTariffId}`),
  );

  const { mutateAsync, isPending } = useOrdersCreate({});

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    mutateAsync({
      fromCityId: Number(data.fromCityId),
      fromLatitude: Number(data.fromLatitude),
      fromLongitude: Number(data.fromLongitude),
      fromAddress: data.fromAddress,
      fromApartment: data.fromApartment,
      fromEntrance: data.fromEntrance,
      fromFloor: data.fromFloor,

      toCityId: Number(data.toCityId),
      toLatitude: Number(data.toLatitude),
      toLongitude: Number(data.toLongitude),
      toAddress: data.toAddress,
      toApartment: data.toApartment,
      toEntrance: data.toEntrance,
      toFloor: data.toFloor,

      tariffId: Number(data.tariffId),

      description: data.description,
      category: data.category,
      isFragile: data.isFragile,
      isDefect: data.isDefect,
      photos: data.photos,
      contentPhotos: data.contentPhotos,
      comment: data.comment,

      width: Number(data.width),
      length: Number(data.length),
      height: Number(data.height),
      weight: Number(data.weight),
    }).then(() => {
      reset();
    });
  };

  return (
    <Form className="flex flex-col pt-4 pr-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-row gap-2 justify-between h-12 items-center w-full">
        <p className="font-bold text-xl">Создать заказ</p>

        <Button isPending={isPending} type="submit" variant="primary">
          Сохранить
        </Button>
      </div>

      <div className="container mt-6 gap-4 flex flex-col max-w-[40rem]">
        {/* FROM CITY */}
        <Controller
          control={control}
          name="fromCityId"
          rules={{ required: "Нужно заполнить поле" }}
          render={({ field }) => (
            <Select
              isInvalid={!!errors.fromCityId?.message}
              selectedKey={selectedCityFromKey}
              onSelectionChange={(key) => {
                setSelectedCityFromKey(key ? String(key) : null);
                field.onChange(key ? Number(key) : undefined);
              }}
            >
              <Select.Trigger>
                <Label>Откуда</Label>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {citiesFrom.data.data.map((item) => (
                    <ListBox.Item key={String(item.id)} id={String(item.id)}>
                      {item.name}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />

        <TextField isInvalid={!!errors.fromLatitude?.message}>
          <Label>Широта</Label>
          <Input {...register("fromLatitude")} />
          <FieldError>{errors.fromLatitude?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.fromLongitude?.message}>
          <Label>Долгота</Label>
          <Input {...register("fromLongitude")} />
          <FieldError>{errors.fromLongitude?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.fromAddress?.message}>
          <Label>Адрес</Label>
          <Input {...register("fromAddress", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.fromAddress?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.fromApartment?.message}>
          <Label>Квартира</Label>
          <Input {...register("fromApartment")} />
          <FieldError>{errors.fromApartment?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.fromEntrance?.message}>
          <Label>Вход</Label>
          <Input {...register("fromEntrance")} />
          <FieldError>{errors.fromEntrance?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.fromFloor?.message}>
          <Label>Этаж</Label>
          <Input {...register("fromFloor")} />
          <FieldError>{errors.fromFloor?.message}</FieldError>
        </TextField>

        {/* TO CITY */}
        <Controller
          control={control}
          name="toCityId"
          rules={{ required: "Нужно заполнить поле" }}
          render={({ field }) => (
            <Select
              isInvalid={!!errors.toCityId?.message}
              onSelectionChange={(key) => {
                field.onChange(key ? Number(key) : undefined);
              }}
            >
              <Select.Trigger>
                <Label>Куда</Label>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {citiesTo.data.data.map((item) => (
                    <ListBox.Item key={String(item.id)} id={String(item.id)}>
                      {item.name}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />

        <TextField isInvalid={!!errors.toLatitude?.message}>
          <Label>Широта</Label>
          <Input {...register("toLatitude")} />
          <FieldError>{errors.toLatitude?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.toLongitude?.message}>
          <Label>Долгота</Label>
          <Input {...register("toLongitude")} />
          <FieldError>{errors.toLongitude?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.toAddress?.message}>
          <Label>Адрес</Label>
          <Input {...register("toAddress", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.toAddress?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.toApartment?.message}>
          <Label>Квартира</Label>
          <Input {...register("toApartment")} />
          <FieldError>{errors.toApartment?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.toEntrance?.message}>
          <Label>Вход</Label>
          <Input {...register("toEntrance")} />
          <FieldError>{errors.toEntrance?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.toFloor?.message}>
          <Label>Этаж</Label>
          <Input {...register("toFloor")} />
          <FieldError>{errors.toFloor?.message}</FieldError>
        </TextField>

        {/* TARIFF */}
        <Controller
          control={control}
          name="tariffId"
          rules={{ required: "Нужно заполнить поле" }}
          render={({ field }) => (
            <Select
              isInvalid={!!errors.tariffId?.message}
              onSelectionChange={(key) => {
                field.onChange(key ? Number(key) : undefined);
              }}
            >
              <Select.Trigger>
                <Label>Тариф</Label>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {tariffs.data.data.map((item) => (
                    <ListBox.Item key={String(item.id)} id={String(item.id)}>
                      {item.name}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />

        <TextField isInvalid={!!errors.description?.message}>
          <Label>Описание</Label>
          <Input {...register("description")} />
          <FieldError>{errors.description?.message}</FieldError>
        </TextField>

        <TextField isInvalid={!!errors.category?.message}>
          <Label>Категория</Label>
          <Input {...register("category", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.category?.message}</FieldError>
        </TextField>

        <Controller
          control={control}
          defaultValue={false}
          name="isFragile"
          render={({ field }) => (
            <Switch isSelected={field.value} onChange={field.onChange}>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Content>
                <Label>Хрупкий</Label>
              </Switch.Content>
            </Switch>
          )}
        />

        <p>Фотографии общие</p>
        <p>Фотографии содержимого</p>

        <TextField isInvalid={!!errors.comment?.message}>
          <Label>Комментарии</Label>
          <Input {...register("comment")} />
          <FieldError>{errors.comment?.message}</FieldError>
        </TextField>

        {selectedTariff?.fields.includes("WIDTH") && (
          <TextField isInvalid={!!errors.width?.message}>
            <Label>Ширина</Label>
            <Input {...register("width")} />
            <FieldError>{errors.width?.message}</FieldError>
          </TextField>
        )}

        {selectedTariff?.fields.includes("LENGTH") && (
          <TextField isInvalid={!!errors.length?.message}>
            <Label>Длина</Label>
            <Input {...register("length")} />
            <FieldError>{errors.length?.message}</FieldError>
          </TextField>
        )}

        {selectedTariff?.fields.includes("HEIGHT") && (
          <TextField isInvalid={!!errors.height?.message}>
            <Label>Высота</Label>
            <Input {...register("height")} />
            <FieldError>{errors.height?.message}</FieldError>
          </TextField>
        )}

        {selectedTariff?.fields.includes("WEIGHT") && (
          <TextField isInvalid={!!errors.weight?.message}>
            <Label>Вес</Label>
            <Input {...register("weight")} />
            <FieldError>{errors.weight?.message}</FieldError>
          </TextField>
        )}
      </div>
    </Form>
  );
}
